/**
 * The client's calendar, as variant D draws it: weeks down, weekdays across, one chip per session.
 *
 * **This is the review step.** Variant D has no "Sprawdź" — the projection the trainer has been
 * watching fill in IS what they check, so the 364-row list that stood here is gone and so is the
 * module that worded it. Everything the old `assignReview` decided that survives the change — the
 * row's title, the generator's own time resolution, the moved flag, the summary line — is decided
 * here instead, on the same inputs, grouped by CALENDAR WEEK rather than by cycle.
 *
 * ── A DateBased cycle IS a calendar week, and that is why the rail can say T1 ────────────────
 * `ScheduleGenerator.ComputeDateBased` places cycle N at `start + (N-1)*7 + (weekday - startWeekday)`,
 * so every occurrence of cycle N lands in the Monday-anchored week containing `start + (N-1)*7`.
 * The rail's `T1..Tn` are therefore the cycles, named the way the mock names them, and nothing here
 * has to count weeks and cycles separately. A MOVED occurrence can leave its own week, which is the
 * one case where the two come apart — and it is drawn where it actually lands, because a projection
 * that showed it in its old row would be showing the trainer a calendar the client will not get.
 *
 * ── The grid spans the plan, and it is not a month ───────────────────────────────────────────
 * From the start date's Monday to the Monday of the last effective date, so a session dragged past
 * the last cycle still has a cell to be in. Collapsed it draws the first five weeks; „Cały plan"
 * draws all of them. A 52-cycle plan is 52 rows, which is what `expanded` is for.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. `t` and `locale` are parameters.
 */

import { addDays, mondayOf, parseIsoDate, toIsoDate } from '~/shared/lib/calendarDate';

import {
  coordinateKey,
  flattenOccurrences,
  toCoordinate,
  type OccurrenceCoordinate,
  type ScheduleCycle,
  type ScheduleOccurrence,
} from './assignSchedule';
import { isStationary, type AssignExecutionMode, type OccurrenceOverride } from './assignRequest';
import { effectiveDate, preStartDrops, type PreStartDrops } from './preStartDrops';
import { WEEKDAY_FALLBACK_KEY } from './stationaryTimes';

export type Translate = (key: string, values?: Record<string, string | number>) => string;

/** How many weeks the collapsed projection draws before „Cały plan" is needed. */
export const COLLAPSED_WEEKS = 5;

/**
 * What a chip IS, in one word.
 *
 * Three states rather than two booleans: a session can be moved, struck, or both — a chip dragged
 * onto a date before the plan starts is a move the server will still withhold — and `withheld`
 * wins, because "this will not happen" outranks "this is not where it was programmed".
 */
export type ChipState = 'planned' | 'moved' | 'withheld';

/** One session, where it actually lands. */
export interface ProjectionChip {
  /** The coordinate key — the chip's identity, the override map's key, the drag payload. */
  readonly key: string;
  readonly coordinate: OccurrenceCoordinate;
  /** The workout's name, the day's, or a positional fallback. Always non-empty. */
  readonly title: string;
  readonly state: ChipState;
  /** "17:30", or `null` for a Remote assignment, which the backend schedules date-only. */
  readonly time: string | null;
  /** The effective date as "YYYY-MM-DD" — what a drop and the popover both write. */
  readonly iso: string;
  /** The generated date, so a restore needs no recomputation. */
  readonly patternIso: string;
  /**
   * The hour the PATTERN gives this session — the standing weekday time, before any override.
   *
   * Beside `time` rather than derived from it, because the two come apart exactly where it matters:
   * a session retimed in the popover carries the trainer's hour in `time` and the weekday's own in
   * here, and an edit that touches only the DATE has to tell "this hour is the standing one, drop
   * it" from "this hour is the one I chose, keep it". Comparing against `time` cannot: it already
   * includes the override, so every retimed session read as unchanged and lost its hour on the next
   * date edit. `null` wherever `time` is — a Remote assignment books no hours at all.
   */
  readonly patternTime: string | null;
  /** "przeniesiona ze śr." — said only on a chip that moved. */
  readonly movedFrom: string | null;
  /** The whole chip as one sentence, for the button's accessible name. */
  readonly label: string;
}

/** One day of the grid. */
export interface ProjectionCell {
  readonly iso: string;
  /** The day of the month, which is all the cell prints. */
  readonly dayOfMonth: number;
  /** "poniedziałek, 28 września" — the cell's accessible name and a drop target's. */
  readonly label: string;
  /** The plan's own first day. Drawn as the one inverted number in the grid. */
  readonly isStart: boolean;
  /** Before the plan begins. Nothing dropped here reaches the client. */
  readonly beforeStart: boolean;
  readonly chips: readonly ProjectionChip[];
}

/** One row: a cycle's own calendar week. */
export interface ProjectionWeek {
  readonly key: string;
  /** "T1". The cycle this week is. */
  readonly label: string;
  readonly cells: readonly ProjectionCell[];
}

/** The whole right-hand panel's copy. */
export interface AssignProjection {
  readonly weeks: readonly ProjectionWeek[];
  /** "pon." … "nd." — the column heads, in the reader's own locale, Monday first. */
  readonly weekdayHeads: readonly string[];
  /** "36 sesji · pon., 28 wrz do pt., 18 gru". */
  readonly summary: string;
  /** Sessions that actually reach the client. The struck ones are NOT in it. */
  readonly sessionCount: number;
  readonly withheldCount: number;
  readonly movedCount: number;
  /** "1 sesja przeniesiona", or `null` when nothing moved. */
  readonly movedNote: string | null;
  /** How many weeks the plan has, whether or not they are drawn. */
  readonly totalWeeks: number;
  /** Whether „Cały plan" has anything left to reveal. */
  readonly truncated: boolean;
  /** Said when the plan generates nothing at all. `null` otherwise. */
  readonly emptyMessage: string | null;
  /**
   * Every chip on the grid, by its key.
   *
   * Built here because it is built here anyway — the loop below already visits every occurrence
   * once. Both readers (the drag monitor, which resolves what was dropped, and the calendar, which
   * paints the lifted one) were otherwise flattening `weeks → cells → chips` into a fresh `Map` on
   * every render, twice.
   */
  readonly chipsByKey: ReadonlyMap<string, ProjectionChip>;
}

export interface ProjectionInput {
  readonly cycles: readonly ScheduleCycle[];
  readonly overrides: ReadonlyMap<string, OccurrenceOverride>;
  readonly stationaryTimes: Readonly<Record<number, string>>;
  readonly mode: AssignExecutionMode;
  readonly startDate: Date;
  /** `false` draws the first five weeks. */
  readonly expanded: boolean;
  readonly locale: string;
  readonly t: Translate;
  /**
   * The flattened occurrences and the withheld set, when the caller has already computed them.
   *
   * The start field needs `preStartDrops` for its own sentence, so the dialog builds both before it
   * asks for a projection — and a 52-cycle plan is 364 occurrences walked twice per keystroke in
   * the search boxes. Omitted, they are computed here from `cycles`, which is what the tests do.
   */
  readonly occurrences?: readonly ScheduleOccurrence[];
  readonly drops?: PreStartDrops;
}

/**
 * The three formatters, built ONCE per call rather than once per cell.
 *
 * A 52-cycle plan is 364 cells and up to 364 chips, and constructing an `Intl.DateTimeFormat` is
 * the expensive part of using one — the locale data lookup happens at construction. The same
 * measurement the old review step recorded, on the same cap.
 */
interface Formatters {
  /** "pon., 28 wrz" — the summary's two ends and a moved chip's origin. */
  readonly short: Intl.DateTimeFormat;
  /** "poniedziałek, 28 września" — a cell's accessible name. */
  readonly full: Intl.DateTimeFormat;
  /** "pon." — a column head. */
  readonly weekdayShort: Intl.DateTimeFormat;
}

function formatters(locale: string): Formatters {
  return {
    short: new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }),
    full: new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }),
    weekdayShort: new Intl.DateTimeFormat(locale, { weekday: 'short' }),
  };
}

/**
 * The Stationary time for one occurrence, resolved exactly the way the generator resolves it.
 *
 * `ScheduleGenerator.ApplyStationaryTime`, step for step: the occurrence's own weekday key, then
 * the fallback key 0, then the first configured entry. A preview that resolved the hour differently
 * would show the trainer a booking their diary is not going to get. Step 3 is the generator's own
 * last resort and is unreachable through this dialog, which fills every slot the plan has.
 */
function resolveTime(
  times: Readonly<Record<number, string>>,
  dayOfWeek: number | null,
): string | null {
  if (dayOfWeek !== null && times[dayOfWeek] !== undefined) return times[dayOfWeek];
  const fallback = times[WEEKDAY_FALLBACK_KEY];
  if (fallback !== undefined) return fallback;
  const [first] = Object.keys(times)
    .map(Number)
    .sort((a, b) => a - b);
  return first === undefined ? null : (times[first] ?? null);
}

function chipTitle(occurrence: ScheduleOccurrence, t: Translate): string {
  const session = occurrence.sessionName.trim();
  if (session !== '') return session;
  const day = occurrence.dayName?.trim();
  if (day !== undefined && day !== '') return day;
  return t('web:assign.live.untitledSession', { n: occurrence.dayOrder });
}

export function assignProjection(input: ProjectionInput): AssignProjection {
  const { cycles, overrides, stationaryTimes, mode, startDate, expanded, locale, t } = input;
  const stationary = isStationary(mode);
  const format = formatters(locale);
  const occurrences = input.occurrences ?? flattenOccurrences(cycles);
  const drops: PreStartDrops = input.drops ?? preStartDrops(occurrences, overrides, startDate);

  const firstMonday = mondayOf(startDate);
  // The chips, by the ISO date they land on — built before the grid, because the grid's LENGTH
  // depends on how far the furthest move went.
  const byDate = new Map<string, ProjectionChip[]>();
  const chipsByKey = new Map<string, ProjectionChip>();
  let movedCount = 0;
  let lastDate = firstMonday;

  for (const occurrence of occurrences) {
    const key = coordinateKey(occurrence);
    const override = overrides.get(key);
    const date = effectiveDate(occurrence, overrides);
    const withheld = drops.withheld.has(key);
    const moved = override !== undefined;
    if (moved) movedCount += 1;
    if (date > lastDate) lastDate = date;

    const title = chipTitle(occurrence, t);
    const patternTime = stationary ? resolveTime(stationaryTimes, occurrence.dayOfWeek) : null;
    const time = stationary ? (override?.time ?? patternTime) : null;
    const movedFrom = moved
      ? t('web:assign.live.movedFrom', { date: format.short.format(occurrence.patternDate) })
      : null;
    const state: ChipState = withheld ? 'withheld' : moved ? 'moved' : 'planned';

    const chip: ProjectionChip = {
      key,
      coordinate: toCoordinate(occurrence),
      title,
      state,
      time,
      iso: toIsoDate(date),
      patternIso: toIsoDate(occurrence.patternDate),
      patternTime,
      movedFrom,
      // One sentence rather than four nodes: the chip is a button, and a button's accessible name
      // is read as a whole. The state is IN the name, because a dashed border is not announced.
      label: t(`web:assign.live.chipLabel.${state}`, {
        title,
        date: format.short.format(date),
        // i18next renders an unsupplied placeholder as `{{time}}`, so every key gets every value
        // and the sentences choose between them.
        time: time ?? '',
        from: format.short.format(occurrence.patternDate),
      }),
    };

    chipsByKey.set(key, chip);
    const iso = chip.iso;
    const cell = byDate.get(iso);
    if (cell === undefined) byDate.set(iso, [chip]);
    else cell.push(chip);
  }

  // Inside a cell, by the hour the client trains, then by the plan's own walk order — so a Remote
  // day (no hours at all) keeps the order the generator produced rather than sorting alphabetically.
  for (const chips of byDate.values()) {
    chips.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? '') || a.key.localeCompare(b.key));
  }

  const totalWeeks = Math.max(cycles.length, weeksBetween(firstMonday, mondayOf(lastDate)));
  const drawnWeeks = expanded ? totalWeeks : Math.min(totalWeeks, COLLAPSED_WEEKS);
  const startIso = toIsoDate(startDate);

  const weeks: ProjectionWeek[] = [];
  for (let week = 0; week < drawnWeeks; week += 1) {
    const monday = addDays(firstMonday, week * 7);
    const cells: ProjectionCell[] = [];
    for (let column = 0; column < 7; column += 1) {
      const date = addDays(monday, column);
      const iso = toIsoDate(date);
      cells.push({
        iso,
        dayOfMonth: date.getDate(),
        label: format.full.format(date),
        isStart: iso === startIso,
        beforeStart: date < startDate,
        chips: byDate.get(iso) ?? [],
      });
    }
    weeks.push({
      key: toIsoDate(monday),
      label: t('web:assign.live.weekLabel', { n: week + 1 }),
      cells,
    });
  }

  const reaching = occurrences.length - drops.count;
  const dates = [...byDate.entries()]
    .filter(([, chips]) => chips.some((chip) => chip.state !== 'withheld'))
    .map(([day]) => day)
    .sort();
  const first = dates[0];
  const last = dates[dates.length - 1];

  return {
    weeks,
    weekdayHeads: Array.from({ length: 7 }, (_, column) =>
      // 4 January 2027 is a Monday, so `4 + column` walks Monday to Sunday. A fixed reference date
      // rather than a hand-written table — the table would be a second source of truth for weekday
      // names `Intl` already answers, in the reader's own locale.
      format.weekdayShort.format(new Date(2027, 0, 4 + column)),
    ),
    summary:
      first === undefined || last === undefined
        ? t('web:assign.live.summaryEmpty')
        : t('web:assign.live.summary', {
            sessions: t('web:assign.live.sessions', { count: reaching }),
            from: format.short.format(parseIsoDate(first) ?? startDate),
            to: format.short.format(parseIsoDate(last) ?? startDate),
          }),
    sessionCount: reaching,
    withheldCount: drops.count,
    movedCount,
    movedNote: movedCount === 0 ? null : t('web:assign.live.movedCount', { count: movedCount }),
    totalWeeks,
    truncated: drawnWeeks < totalWeeks,
    emptyMessage: occurrences.length === 0 ? t('web:assign.live.empty') : null,
    chipsByKey,
  };
}

/**
 * How many Monday-weeks the span covers, both ends inclusive.
 *
 * Counted by WALKING rather than by dividing a millisecond difference: a spring-forward week is
 * 167 hours long, so the division truncates to one week short twice a year and the projection
 * silently loses its last row. `addDays` rolls the calendar, which is what a week means here.
 */
function weeksBetween(firstMonday: Date, lastMonday: Date): number {
  let weeks = 1;
  let cursor = firstMonday;
  while (cursor < lastMonday) {
    cursor = addDays(cursor, 7);
    weeks += 1;
  }
  return weeks;
}

